import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import { connect, useDispatch } from "react-redux";
import { Button, Container, Grid, makeStyles, Typography, MenuItem } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { getWorker, getWorkers, postWorker, putWorker } from "../../store/actions/worker/worker_action";
import { REACT_ROUTES } from "../../constants/react_routes";
import { API_ROUTES } from "../../constants/api_routes";
import { WORKER_TYPES } from "../../constants/constants";
import history from "../../services/history";
import TextInput from "../ui_components/text_input";
import Autocomplete from "../ui_components/autocomplete_controlled";
import SelectInput from "../ui_components/select_input";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3, 2)
    },
    typography: {
        margin: theme.spacing(3, 0, 2)
    },
    form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(1),
        "& p": {
            color: "#bf1650",
            "&::before": {
                display: "inline",
                content: '"⚠ "'
            }
        }
    }
}));

const showErrorsAndMessages = (violations, enqueueSnackbar, setError) => {
    if (!violations) {
        enqueueSnackbar("Uspešno ste sačuvali radnika", { variant: "success" });
        return history.push(REACT_ROUTES.WORKERS);
    }
    for (const violation of violations) {
        enqueueSnackbar(violation?.message, { variant: "warning" });
        if (violation?.propertyPath) {
            setError(violation?.propertyPath, { message: violation?.message });
        }
    }
};

const Worker = ({ workers, isManager, isAdmin }) => {
    const { uuid } = useParams();
    const { container, typography, form } = useStyles();
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
        setError
    } = useForm();
    const dispatch = useDispatch();
    const [user, setUser] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        uuid &&
            (async () => {
                const worker = await getWorker(uuid);
                if (worker?.user) {
                    setUser(worker.user);
                }
                for (const key in worker) {
                    setValue(key, worker[key]);
                }
            })();
        !workers.collection.length && dispatch(getWorkers());
    }, [uuid]);

    const handleUserObject = (object) => {
        setUser(object);
        setValue("user", object?.["@id"] || null);
    };

    const onSubmit = async (data) => {
        if (uuid) {
            const { violations } = await dispatch(putWorker(uuid, data));
            return showErrorsAndMessages(violations, enqueueSnackbar, setError);
        }
        const { violations } = await dispatch(postWorker(data));
        return showErrorsAndMessages(violations, enqueueSnackbar, setError);
    };

    const handleClickBackToList = () => {
        history.push(REACT_ROUTES.WORKERS);
    };

    return (
        <Container className={container}>
            <Typography align="center" variant="h4" className={typography}>
                Radnik
            </Typography>
            <Grid container justify="center" spacing={3}>
                <Grid item>
                    <Button color="primary" size="small" variant="contained" onClick={handleSubmit(onSubmit)} type="button">
                        Sačuvaj
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" size="small" color="primary" onClick={handleClickBackToList}>
                        Nazad
                    </Button>
                </Grid>
            </Grid>
            <form className={form}>
                <Grid container justify="center" spacing={3}>
                    <Grid item xs={8}>
                        <TextInput capitalize control={control} name="firstName" label="Ime" rules={{ required: "Unesite ime" }} />
                        <ErrorMessage errors={errors} name="firstName" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput capitalize control={control} name="lastName" label="Prezime" rules={{ required: "Unesite prezime" }} />
                        <ErrorMessage errors={errors} name="lastName" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput control={control} name="phone" label="Telefon" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput control={control} name="email" label="Email" />
                        <ErrorMessage errors={errors} name="email" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            name="initialTextForReport"
                            control={control}
                            label="Inicijalni tekst za isveštaj"
                            multiline
                            rows={5}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput control={control} name="percentage" label="Procenat %" />
                    </Grid>
                    <Grid item xs={8}>
                        <SelectInput name="workerType" label="Tip radnika" control={control}>
                            {WORKER_TYPES.map((type, index) => (
                                <MenuItem key={index} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </SelectInput>
                    </Grid>
                    {(isAdmin === true || isManager === true) && (
                        <Grid item xs={8}>
                            <Autocomplete
                                control={control}
                                name="user"
                                label="Korisnik"
                                api={API_ROUTES.USERS}
                                handleObject={handleUserObject}
                                preloadValue={user}
                            />
                            <ErrorMessage errors={errors} name="user" as="p" />
                        </Grid>
                    )}
                </Grid>
            </form>
        </Container>
    );
};

Worker.propTypes = {
    workers: PropTypes.object,
    isManager: PropTypes.bool,
    isAdmin: PropTypes.bool
};

const mapStateToProps = (state) => ({
    workers: state.workers,
    isManager: state.user.isManager,
    isAdmin: state.user.isAdmin
});

export default connect(mapStateToProps)(Worker);
